import { useState, useRef, useEffect, useCallback } from "react";
import { Send, Square, Database, Check, Mic, MicOff, Plus, Upload, Mail, HardDrive, Cable, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import ManualUploads from "@/components/admin/ManualUploads";
import GmailConnector from "@/components/admin/GmailConnector";
import GoogleDriveConnector from "@/components/admin/GoogleDriveConnector";
import HowItWorksDialog from "@/components/HowItWorksDialog";

type ConnectorDialog = "manual-upload" | "gmail" | "google-drive" | null;

interface DataSource {
  id: string;
  name: string;
  enabled: boolean;
}

interface ChatInputProps {
  onSend: (message: string) => void;
  disabled?: boolean;
  loading?: boolean;
  onStop?: () => void;
  dataSources?: DataSource[];
  selectedSourceIds?: string[];
  onSelectedSourceIdsChange?: (ids: string[]) => void;
  focusTrigger?: number;
}

const CONNECTOR_TITLES: Record<Exclude<ConnectorDialog, null>, string> = {
  "manual-upload": "Upload CSV / XLS",
  "gmail": "Gmail",
  "google-drive": "Google Drive",
};

const ChatInput = ({
  onSend,
  disabled,
  loading,
  onStop,
  dataSources = [],
  selectedSourceIds = [],
  onSelectedSourceIdsChange,
  focusTrigger,
}: ChatInputProps) => {
  const [input, setInput] = useState("");
  const [isListening, setIsListening] = useState(false);
  const [openConnector, setOpenConnector] = useState<ConnectorDialog>(null);
  const [addDataOpen, setAddDataOpen] = useState(false);
  const [howItWorksOpen, setHowItWorksOpen] = useState(false);
  const navigate = useNavigate();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const recognitionRef = useRef<any>(null);
  const baseInputRef = useRef("");

  const handleSubmit = () => {
    const trimmed = input.trim();
    if (!trimmed || disabled) return;
    onSend(trimmed);
    setInput("");
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 200) + "px";
    }
  }, [input]);

  const initialRender = useRef(true);
  useEffect(() => {
    // Skip auto-focus on initial mount (prevents keyboard popup on mobile welcome screen)
    if (initialRender.current) {
      initialRender.current = false;
      return;
    }
    if (focusTrigger !== undefined) {
      setTimeout(() => textareaRef.current?.focus(), 50);
    }
  }, [focusTrigger]);

  const toggleSource = (id: string) => {
    if (!onSelectedSourceIdsChange) return;
    if (selectedSourceIds.includes(id)) {
      onSelectedSourceIdsChange(selectedSourceIds.filter((s) => s !== id));
    } else {
      onSelectedSourceIdsChange([...selectedSourceIds, id]);
    }
  };

  const allSelected = dataSources.length > 0 && selectedSourceIds.length === dataSources.length;

  const speechSupported = typeof window !== "undefined" &&
    ("SpeechRecognition" in window || "webkitSpeechRecognition" in window);

  const toggleListening = () => {
    if (isListening) {
      const ref = recognitionRef.current;
      recognitionRef.current = null; // prevent auto-restart in onend
      ref?.stop();
      setIsListening(false);
      return;
    }

    const SpeechRecognitionCtor =
      (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognitionCtor) return;

    baseInputRef.current = input;

    const startRecognition = () => {
      const recognition = new SpeechRecognitionCtor();
      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.lang = "en-US";

      recognition.onstart = () => setIsListening(true);

      recognition.onend = () => {
        if (recognitionRef.current === recognition) {
          try {
            const next = new SpeechRecognitionCtor();
            next.continuous = false;
            next.interimResults = true;
            next.lang = "en-US";
            next.onstart = recognition.onstart;
            next.onend = recognition.onend;
            next.onerror = recognition.onerror;
            next.onresult = recognition.onresult;
            recognitionRef.current = next;
            next.start();
          } catch {
            setIsListening(false);
            recognitionRef.current = null;
          }
        }
      };

      recognition.onerror = (e: any) => {
        if (e.error === "no-speech") return;
        setIsListening(false);
        recognitionRef.current = null;
      };

      recognition.onresult = (event: any) => {
        const transcript = Array.from(event.results as SpeechRecognitionResultList)
          .map((r: any) => r[0].transcript)
          .join("");
        const prefix = baseInputRef.current;

        if (event.results[event.results.length - 1].isFinal) {
          baseInputRef.current = (prefix ? prefix + " " : "") + transcript.trim();
          setInput(baseInputRef.current);
        } else {
          setInput(prefix ? prefix + " " + transcript.trim() : transcript.trim());
        }
      };

      recognitionRef.current = recognition;
      recognition.start();
    };

    startRecognition();
  };

  useEffect(() => {
    return () => {
      recognitionRef.current?.stop();
    };
  }, []);

  const openConnectorDialog = (connector: ConnectorDialog) => {
    setAddDataOpen(false);
    setOpenConnector(connector);
  };

  return (
    <div className="border-t border-border bg-background p-4">
      {/* Connectors picker + How it works */}
      <div className="mx-auto mb-2 flex max-w-3xl items-center gap-2">
        {dataSources.length > 0 && (
          <>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="gap-1.5 h-7 text-xs">
                  <Database className="h-3 w-3" />
                  Connectors
                  <Badge variant="secondary" className="ml-1 h-4 px-1 text-[10px]">
                    {selectedSourceIds.length}/{dataSources.length}
                  </Badge>
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-64 p-2" sideOffset={4}>
                <div className="mb-1 px-2 py-1 text-xs font-medium text-muted-foreground">
                  Select data sources for this chat
                </div>
                <button
                  onClick={() => {
                    if (!onSelectedSourceIdsChange) return;
                    if (allSelected) {
                      onSelectedSourceIdsChange([]);
                    } else {
                      onSelectedSourceIdsChange(dataSources.map((s) => s.id));
                    }
                  }}
                  className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent transition-colors"
                >
                  <div className={cn(
                    "flex h-4 w-4 items-center justify-center rounded border",
                    allSelected ? "bg-primary border-primary" : "border-input"
                  )}>
                    {allSelected && <Check className="h-3 w-3 text-primary-foreground" />}
                  </div>
                  <span className="font-medium">Select all</span>
                </button>
                <div className="my-1 border-t border-border" />
                {dataSources.map((source) => {
                  const selected = selectedSourceIds.includes(source.id);
                  return (
                    <button
                      key={source.id}
                      onClick={() => toggleSource(source.id)}
                      className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent transition-colors"
                    >
                      <div className={cn(
                        "flex h-4 w-4 items-center justify-center rounded border",
                        selected ? "bg-primary border-primary" : "border-input"
                      )}>
                        {selected && <Check className="h-3 w-3 text-primary-foreground" />}
                      </div>
                      <span>{source.name}</span>
                    </button>
                  );
                })}
              </PopoverContent>
            </Popover>
            {selectedSourceIds.length === 0 && (
              <span className="text-xs text-muted-foreground">No connectors selected — AI won't use external tools</span>
            )}
          </>
        )}
        <div className="flex-1" />
        <button
          onClick={() => setHowItWorksOpen(true)}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors"
        >
          <HelpCircle className="h-3.5 w-3.5" />
          How it works?
        </button>
      </div>
      <div className="mx-auto flex max-w-3xl items-end gap-2 rounded-2xl border border-input bg-card p-2">
        <Popover open={addDataOpen} onOpenChange={setAddDataOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className="shrink-0 rounded-xl"
              title="Add data"
            >
              <Plus className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-52 p-1" sideOffset={4}>
            <button
              onClick={() => openConnectorDialog("manual-upload")}
              className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm hover:bg-accent transition-colors"
            >
              <Upload className="h-4 w-4 text-muted-foreground" />
              Upload CSV / XLS
            </button>
            <button
              onClick={() => openConnectorDialog("gmail")}
              className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm hover:bg-accent transition-colors"
            >
              <Mail className="h-4 w-4 text-muted-foreground" />
              Gmail
            </button>
            <button
              onClick={() => openConnectorDialog("google-drive")}
              className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm hover:bg-accent transition-colors"
            >
              <HardDrive className="h-4 w-4 text-muted-foreground" />
              Google Drive
            </button>
            <div className="my-1 border-t border-border" />
            <button
              onClick={() => { setAddDataOpen(false); navigate("/data-connectors"); }}
              className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm hover:bg-accent transition-colors"
            >
              <Cable className="h-4 w-4 text-muted-foreground" />
              Other connectors
            </button>
          </PopoverContent>
        </Popover>
        <Textarea
          ref={textareaRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Send a message..."
          disabled={disabled}
          className="min-h-[88px] md:min-h-[44px] max-h-[200px] resize-none border-0 bg-transparent shadow-none focus-visible:ring-0"
          rows={1}
        />
        {speechSupported && !loading && (
          <Button
            type="button"
            onClick={toggleListening}
            disabled={disabled}
            size="icon"
            variant={isListening ? "destructive" : "outline"}
            className={cn(
              "shrink-0 rounded-xl",
              isListening && "animate-pulse"
            )}
            title={isListening ? "Stop listening" : "Speak a message"}
          >
            {isListening ? (
              <MicOff className="h-4 w-4" />
            ) : (
              <Mic className="h-4 w-4" />
            )}
          </Button>
        )}
        {loading ? (
          <Button
            onClick={onStop}
            size="icon"
            variant="destructive"
            className="shrink-0 rounded-xl"
            title="Stop generating"
          >
            <Square className="h-3.5 w-3.5" />
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            disabled={!input.trim() || disabled}
            size="icon"
            className="shrink-0 rounded-xl"
          >
            <Send className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Connector popup dialog */}
      <Dialog open={openConnector !== null} onOpenChange={(open) => { if (!open) setOpenConnector(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{openConnector ? CONNECTOR_TITLES[openConnector] : ""}</DialogTitle>
            <DialogDescription className="sr-only">
              {openConnector ? `Manage your ${CONNECTOR_TITLES[openConnector]} connection` : ""}
            </DialogDescription>
          </DialogHeader>
          {openConnector === "manual-upload" && <ManualUploads />}
          {openConnector === "gmail" && <GmailConnector />}
          {openConnector === "google-drive" && <GoogleDriveConnector />}
        </DialogContent>
      </Dialog>

      <HowItWorksDialog open={howItWorksOpen} onOpenChange={setHowItWorksOpen} />
    </div>
  );
};

export default ChatInput;
